import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaSpinner, FaUpload } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { TrainingCertification, TrainingType, TrainingInstitute } from '../types';
import { uploadFilesWithPresignedUrls } from '../../../libs/presigned-upload';

interface EditCertificationOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  certification: TrainingCertification | null;
  trainingTypes?: TrainingType[];
  institutes?: TrainingInstitute[];
  loading?: boolean;
}

const toDateInput = (dateString?: string) => {
  if (!dateString) return '';
  // DATEONLY comes as "YYYY-MM-DD"; ISO strings also slice cleanly to the date part.
  return String(dateString).slice(0, 10);
};

export const EditCertificationOffcanvas: React.FC<EditCertificationOffcanvasProps> = ({ isOpen, onClose, onSubmit, certification, trainingTypes = [], institutes = [], loading = false }) => {
  const [formData, setFormData] = useState({
    training_type_id: '', training_institute_id: '',
    certificate_number: '', training_date: '', expiry_date: '',
    training_amount: '', card_front_url: '', card_back_url: '',
  });
  const [cardFrontFile, setCardFrontFile] = useState<File | null>(null);
  const [cardBackFile, setCardBackFile] = useState<File | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  useEffect(() => {
    if (certification && isOpen) {
      setFormData({
        training_type_id: certification.training_type_id ? String(certification.training_type_id) : '',
        training_institute_id: certification.training_institute_id ? String(certification.training_institute_id) : '',
        certificate_number: certification.certificate_number || '',
        training_date: toDateInput(certification.training_date),
        expiry_date: toDateInput(certification.expiry_date),
        training_amount: certification.training_amount?.toString() || '',
        card_front_url: certification.card_front_url || '',
        card_back_url: certification.card_back_url || '',
      });
      setCardFrontFile(null);
      setCardBackFile(null);
    }
  }, [certification, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!certification) return;
    if (!formData.training_type_id) { toast.error('Training type is required'); return; }
    if (!formData.training_institute_id) { toast.error('Institute is required'); return; }
    if (!formData.training_date) { toast.error('Training date is required'); return; }
    if (!formData.expiry_date) { toast.error('Expiry date is required'); return; }

    setUploadingFiles(true);
    try {
      let cardFrontUrl = formData.card_front_url.trim();
      let cardBackUrl = formData.card_back_url.trim();
      if (cardFrontFile) {
        const [url] = await uploadFilesWithPresignedUrls([cardFrontFile]);
        if (url) cardFrontUrl = url;
      }
      if (cardBackFile) {
        const [url] = await uploadFilesWithPresignedUrls([cardBackFile]);
        if (url) cardBackUrl = url;
      }

      const submitData: any = {
        id: certification.id,
        training_type_id: Number(formData.training_type_id),
        training_institute_id: Number(formData.training_institute_id),
        training_date: formData.training_date,
        expiry_date: formData.expiry_date,
      };
      if (formData.certificate_number.trim()) submitData.certificate_number = formData.certificate_number.trim();
      submitData.training_amount = formData.training_amount ? Number(formData.training_amount) : null;
      if (cardFrontUrl) submitData.card_front_url = cardFrontUrl;
      if (cardBackUrl) submitData.card_back_url = cardBackUrl;
      onSubmit(submitData);
    } catch (err: any) {
      console.error('Certification update failed:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update certification');
    } finally {
      setUploadingFiles(false);
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen && !loading && !uploadingFiles) onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, uploadingFiles, onClose]);

  if (!isOpen || !certification) return null;

  const busy = loading || uploadingFiles;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={!busy ? onClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Edit Certification</h2>
            <p className="text-sm text-gray-600 mt-1">Update certification details.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded" disabled={busy}><FaTimes className="w-5 h-5 text-gray-600" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Training Request:</span><p className="font-medium">{certification.training_request?.request_id || "-"}</p></div>
              <div><span className="text-gray-500">Candidate:</span><p className="font-medium">{certification.training_request?.candidate_name || "-"}</p></div>
              <div><span className="text-gray-500">Deduction Status:</span><p className="font-medium capitalize">{certification.deduction_status}</p></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Training Type *</label>
                <select name="training_type_id" value={formData.training_type_id} onChange={handleInputChange} disabled={busy} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white">
                  <option value="">Select type</option>
                  {trainingTypes.map(t => <option key={t.id} value={t.id}>{t.training_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institute *</label>
                <select name="training_institute_id" value={formData.training_institute_id} onChange={handleInputChange} disabled={busy} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white">
                  <option value="">Select institute</option>
                  {institutes.map(i => <option key={i.id} value={i.id}>{i.institute_name}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Number</label>
              <input type="text" name="certificate_number" value={formData.certificate_number} onChange={handleInputChange} disabled={busy} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Training Date *</label>
                <input type="date" name="training_date" value={formData.training_date} onChange={handleInputChange} disabled={busy} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date *</label>
                <input type="date" name="expiry_date" value={formData.expiry_date} onChange={handleInputChange} disabled={busy} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Training Amount</label>
              <input type="number" name="training_amount" value={formData.training_amount} onChange={handleInputChange} disabled={busy} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="5900.00" step="0.01" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Front Image</label>
              <label className={`flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm transition-colors ${busy ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                <FaUpload className="text-gray-400 flex-shrink-0" />
                <span className="truncate text-gray-600">{cardFrontFile ? cardFrontFile.name : (formData.card_front_url ? 'Replace card front image' : 'Click to upload card front image')}</span>
                <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => setCardFrontFile(e.target.files?.[0] || null)} />
              </label>
              {formData.card_front_url && !cardFrontFile && (
                <a href={formData.card_front_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-blue-600 hover:underline">View current front image</a>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Back Image</label>
              <label className={`flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm transition-colors ${busy ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                <FaUpload className="text-gray-400 flex-shrink-0" />
                <span className="truncate text-gray-600">{cardBackFile ? cardBackFile.name : (formData.card_back_url ? 'Replace card back image' : 'Click to upload card back image')}</span>
                <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => setCardBackFile(e.target.files?.[0] || null)} />
              </label>
              {formData.card_back_url && !cardBackFile && (
                <a href={formData.card_back_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-blue-600 hover:underline">View current back image</a>
              )}
            </div>
          </form>
        </div>

        <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
            <button type="button" onClick={handleSubmit} disabled={busy} className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-950 disabled:opacity-50 flex items-center gap-2">
              {busy ? (<><FaSpinner className="w-4 h-4 animate-spin" />{uploadingFiles ? 'Uploading...' : 'Updating...'}</>) : (<><FaSave className="w-4 h-4" />Update Certification</>)}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
